import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router'
import { useNotifications } from '../api/hooks'
import { BottomSheet, Icon, OfflineBanner, ToastViewport } from '../components'
import { cx } from '../lib/cx'
import { officeTitles, useSession } from '../session/useSession'
import { BrandMark } from './BrandMark'
import { CONSOLE_NAV, MEMBER_NAV, OFFICER_NAV, OFFICER_TOOLS, type NavItem } from './navigation'
import './SignedInShell.css'

const DevPanel = lazy(() => import('./DevPanel').then((m) => ({ default: m.DevPanel })))

/**
 * The signed-in shell (PRD 16): her group and the bell at the top; five items for her role
 * in the bottom bar on phones, a side rail on tablets, and on desktops the side navigation
 * with the officer console. Development builds add the dev tools (her phone, the clock).
 */
export function SignedInShell() {
  const { t } = useTranslation()
  const { me, membership, mode, groupId, devTools, setGroup } = useSession()
  const location = useLocation()
  const navigate = useNavigate()
  const mainRef = useRef<HTMLElement>(null)
  const shownPath = useRef(location.pathname)
  const [groupsOpen, setGroupsOpen] = useState(false)
  const notifications = useNotifications(groupId)
  const unread = notifications.data?.unread ?? 0

  useEffect(() => {
    if (shownPath.current === location.pathname) return
    shownPath.current = location.pathname
    mainRef.current?.focus({ preventScroll: true })
  }, [location.pathname])

  const officer = mode === 'officer'
  const items = officer ? OFFICER_NAV : MEMBER_NAV
  const memberships = me?.memberships ?? []

  const link = (item: NavItem, place: 'bar' | 'side') => (
    <li key={item.to}>
      <NavLink to={item.to} end={item.end} className={cx('signed-nav__link', place === 'side' && 'signed-nav__link--tool')}>
        <Icon name={item.icon} />
        <span className="signed-nav__label">{t(`shell.nav.${item.label}`)}</span>
      </NavLink>
    </li>
  )

  return (
    <div className={cx('signed', officer && 'signed--officer')}>
      <a className="skip-link" href="#main">
        {t('app.skipToContent')}
      </a>
      <header className="signed-header">
        <Link to="/app" className="signed-header__brand" aria-label={t('shell.homeLink')}>
          <BrandMark size={32} />
        </Link>
        <div className="signed-header__group">
          {memberships.length > 1 ? (
            <button type="button" className="signed-header__switch" onClick={() => setGroupsOpen(true)}
              aria-haspopup="dialog">
              <span className="signed-header__name">{membership?.group.name}</span>
              <Icon name="unfold_more" size={20} />
            </button>
          ) : (
            <span className="signed-header__name">{membership?.group.name}</span>
          )}
          <span className="signed-header__who">
            {membership?.label}
            {officer && officeTitles(membership).length ? ` · ${officeTitles(membership).join(', ')}` : ''}
          </span>
        </div>
        {devTools && (
          <Suspense fallback={null}>
            <DevPanel />
          </Suspense>
        )}
        {/* The name comes from the content, so it always includes what the badge shows ("Notifications 9+ new"). */}
        <NavLink to="/app/notifications" className="signed-header__bell">
          <Icon name="notifications" />
          <span className="visually-hidden">{t('shell.notifications')}</span>
          {unread > 0 && ' '}
          {unread > 0 && (
            <span className="signed-header__badge">
              {unread > 9 ? '9+' : unread}{' '}
              <span className="visually-hidden">{t('shell.newSuffix')}</span>
            </span>
          )}
        </NavLink>
      </header>
      <OfflineBanner />
      <nav className="signed-nav" aria-label={t('shell.mainNav')}>
        <ul className="signed-nav__primary">{items.map((item) => link(item, 'bar'))}</ul>
        {officer && (
          <div className="signed-nav__console">
            <p className="signed-nav__heading">{t('shell.toolsHeading')}</p>
            <ul>{OFFICER_TOOLS.map((item) => link(item, 'side'))}</ul>
            <p className="signed-nav__heading">{t('shell.consoleHeading')}</p>
            <ul>{CONSOLE_NAV.map((item) => link(item, 'side'))}</ul>
          </div>
        )}
      </nav>
      <main id="main" ref={mainRef} tabIndex={-1} className="signed-main">
        <Outlet />
      </main>
      <BottomSheet open={groupsOpen} onClose={() => setGroupsOpen(false)} title={t('shell.switchGroup')}>
        <ul className="group-list">
          {memberships.map((m) => (
            <li key={m.id}>
              <button type="button" className="group-list__item" aria-current={m.group.id === groupId ? 'true' : undefined}
                onClick={() => {
                  setGroup(m.group.id)
                  setGroupsOpen(false)
                  navigate('/app')
                }}>
                <span>
                  <span className="group-list__name">{m.group.name}</span>
                  <span className="group-list__who">{m.label}</span>
                </span>
                {m.group.id === groupId && <Icon name="check" />}
              </button>
            </li>
          ))}
        </ul>
      </BottomSheet>
      <ToastViewport />
      <ScrollRestoration />
    </div>
  )
}
